import { useState, useEffect } from "react";
import type { Editor } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react";

interface MobileSelectionPopoverProps {
  editor: Editor | null;
  onCite?: () => void;
  /** When provided, renders a 🔗 button inside the popover. Demoted from
   *  the keyboard accessory per Claude Design 2026-05-09 trim §A
   *  ("🔗 — Cite-adjacent action; demote to selection popover"). */
  onLink?: () => void;
  onMore?: () => void;
}

/**
 * iOS-native-style selection popover (Claude Design 2026-05-09 mobile-editing-canvas
 * direction D). Anchored above any non-empty text selection inside a TipTap
 * editor. Renders mark toggles + Cite + a `⋯` More target.
 *
 * Mobile-only (matches `(max-width: 1024px)` per the StudioMobile boundary in
 * `feedback_mobile_component_separation.md`). Desktop has the dedicated
 * `StudioFormattingToolbar` and doesn't need a hovering popover.
 *
 * Tap targets are 44px high to clear iOS HIG. The popover hides itself when
 * the selection collapses or the editor blurs.
 */
export function MobileSelectionPopover({
  editor, onCite, onLink, onMore,
}: MobileSelectionPopoverProps) {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 1024px)");
    setIsMobile(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  if (!editor || !isMobile) return null;

  return (
    <BubbleMenu
      editor={editor}
      // Formatting a document that can't be typed into (a viewer, or any phone: the
      // mobile reader is read-only) is not on offer.
      shouldShow={({ editor: ed, from, to }) => from !== to && ed.isEditable}
      className="mobile-selection-popover"
      updateDelay={0}
    >
      <div
        className="flex items-stretch overflow-hidden rounded-[10px] shadow-[0_8px_28px_rgba(0,0,0,0.5)]"
        style={{ background: "#2a2a2c", color: "#fafafa" }}
        role="toolbar"
        aria-label="Selection format"
      >
        <PopoverButton
          editor={editor}
          mark="bold"
          label="Bold"
          render={(active) => (
            <span className="font-bold" style={{ fontFamily: "Charter, Georgia, serif", color: active ? "#a78bfa" : "#fafafa" }}>B</span>
          )}
        />
        <Divider />
        <PopoverButton
          editor={editor}
          mark="italic"
          label="Italic"
          render={(active) => (
            <span className="italic" style={{ fontFamily: "Charter, Georgia, serif", color: active ? "#a78bfa" : "#fafafa" }}>I</span>
          )}
        />
        <Divider />
        <PopoverButton
          editor={editor}
          mark="underline"
          label="Underline"
          render={(active) => (
            <span className="underline" style={{ color: active ? "#a78bfa" : "#fafafa" }}>U</span>
          )}
        />
        <Divider />
        <PopoverButton
          editor={editor}
          mark="code"
          label="Code"
          render={(active) => (
            <span style={{ fontFamily: "var(--lilia-font-mono)", fontSize: 13, color: active ? "#a78bfa" : "#fafafa" }}>{"</>"}</span>
          )}
        />
        {onLink && (
          <>
            <Divider />
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onLink(); }}
              className="min-w-[44px] min-h-[44px] px-3 flex items-center justify-center text-[13px] font-medium"
              aria-label="Insert link"
            >
              <span style={{ fontSize: 15 }}>🔗</span>
            </button>
          </>
        )}
        {onCite && (
          <>
            <Divider />
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onCite(); }}
              className="min-w-[44px] min-h-[44px] px-3 flex items-center justify-center text-[13px] font-medium"
            >
              Cite
            </button>
          </>
        )}
        {onMore && (
          <>
            <Divider />
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onMore(); }}
              className="min-w-[44px] min-h-[44px] px-3 flex items-center justify-center text-[16px] font-semibold"
              style={{ color: "#a78bfa" }}
              aria-label="More format options"
            >
              ⋯
            </button>
          </>
        )}
      </div>
    </BubbleMenu>
  );
}

function PopoverButton({
  editor, mark, label, render,
}: {
  editor: Editor;
  mark: string;
  label: string;
  render: (active: boolean) => React.ReactNode;
}) {
  const active = editor.isActive(mark);
  return (
    <button
      type="button"
      onMouseDown={(e) => {
        // mouseDown so the editor doesn't lose its selection on tap
        e.preventDefault();
        editor.chain().focus().toggleMark(mark).run();
      }}
      className="min-w-[44px] min-h-[44px] px-3 flex items-center justify-center text-[14px]"
      aria-label={label}
      aria-pressed={active}
    >
      {render(active)}
    </button>
  );
}

function Divider() {
  return <span aria-hidden style={{ width: 1, background: "#1f1f22", flexShrink: 0 }} />;
}
